import { useEffect, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { SidebarProvider } from '@/components/ui/sidebar'
import Navbar from '@/components/Layout/Navbar'
import OGESidebar from '@/components/OGE/Sidebar/OGESidebar'
import OGESidebarHeader from '@/components/OGE/Sidebar/OGESidebarHeader'
import { PrepProfileCard } from '@/components/OGE/Sidebar/PrepProfileCard'
import { PrepProfileModal } from '@/components/OGE/Sidebar/PrepProfileModal'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getAccessToken } from '@/utils/authUtils'
import {
  connectCharterPrepMonitorWS,
  disconnectCharterPrepMonitorWS,
} from '../../redux/asyncThunks/charterMonitorThunks'
import { CharterChatMessages } from '../Shared/CharterChatMessages'
import type { CharterDisplayMessage } from '../Shared/CharterChatMessages'
import { SectionProgressBar } from '../Phase1/SectionProgressBar'
import { CharterCollaborationPanel } from '../Shared/CharterCollaborationPanel'
import type { PrepChatMessage } from '../../redux/types/charterWsTypes'
import type { StructuredProfile } from '../../redux/types/charterTypes'

interface PrepMonitorViewProps {
  memberId: string
  memberName: string
  roomId: string
  inviteCode: string
  teamId: string
}

const DEFAULT_SECTION_ORDER = [
  'skills', 'goals', 'obstacles', 'commitments',
  'role_preferences', 'communication_prefs', 'tool_familiarity',
  'meeting_prefs', 'boundaries', 'leadership_preference', 'conflict_decision_prefs',
]

function toPrepDisplayMessages(
  messages: PrepChatMessage[],
  memberName: string
): CharterDisplayMessage[] {
  return messages.map((m) => ({
    id: m.id,
    content: m.content,
    isUser: m.role === 'user',
    isAgent: m.role === 'assistant',
    senderName: m.role === 'user' ? memberName : 'Preparation Coach',
    sectionKey: m.section_key ?? null,
    isStreaming: m.is_streaming,
    createdAt: m.created_at,
  }))
}

export function PrepMonitorView({
  memberId,
  memberName,
  roomId,
  inviteCode,
  teamId,
}: PrepMonitorViewProps) {
  const dispatch = useAppDispatch()
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)

  const session = useAppSelector((s) => s.charterPrepMonitor.sessions[memberId])

  useEffect(() => {
    const token = getAccessToken()
    if (!token || !roomId || !inviteCode || !teamId || !memberId) return
    dispatch(connectCharterPrepMonitorWS({ jwtToken: token, roomId, inviteCode, teamId, memberId }))
    return () => {
      dispatch(disconnectCharterPrepMonitorWS({ memberId }))
    }
  }, [dispatch, roomId, inviteCode, teamId, memberId])

  const messages = session?.messages ?? []
  const completedSections = session?.completedSections ?? {}
  const activeSectionKey = session?.activeSectionKey ?? null
  const sectionOrder = session?.sectionOrder?.length ? session.sectionOrder : DEFAULT_SECTION_ORDER
  const profile = session?.profile as StructuredProfile | null
  const sharingMode = session?.sharingMode ?? 'auto'
  const profileApproved = session?.profileApproved ?? false
  const isConnected = session?.isConnected ?? false
  const isStreaming = session?.isStreaming ?? false

  const displayMessages = toPrepDisplayMessages(messages, memberName)

  const sidebarContent = (
    <div className='px-2 space-y-4'>
      <div>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
          Your Progress
        </p>
        <SectionProgressBar
          sectionOrder={sectionOrder}
          completedSections={completedSections}
          activeSectionKey={activeSectionKey}
        />
      </div>

      {profile && (
        <>
          <Separator />
          <PrepProfileCard
            profile={profile}
            sharingMode={sharingMode}
            profileApproved={profileApproved}
            onClick={() => setIsProfileModalOpen(true)}
          />
        </>
      )}
    </div>
  )

  return (
    <>
    <SidebarProvider className='h-screen'>
      <OGESidebar
        header={
          <OGESidebarHeader
            userName={memberName}
            avatarSrc=''
            subText='Individual Prep — Phase 1'
          />
        }
        content={sidebarContent}
        chatContent={
          <CharterCollaborationPanel type='prep' isConnected={isConnected} />
        }
        initialPanel='game'
      />

      <div className='flex flex-col w-full h-full overflow-auto'>
        {/* Phase info bar */}
        <div className='sticky top-0 z-10 px-14 py-2 ml-[2.5rem]'>
          <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
            <div className='flex items-center gap-2'>
              <Sparkles className='h-5 w-5 text-indigo-500' />
              <div>
                <h3 className='text-sm font-semibold text-dark-blue'>Individual Prep</h3>
                <p className='text-xs text-gray-500'>Phase 1 — Preparation Coach</p>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              {isStreaming && (
                <span className='flex items-center gap-1 text-xs text-indigo-500'>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  Streaming…
                </span>
              )}
              {!isConnected && (
                <Badge variant='outline' className='text-xs text-amber-600 border-amber-300'>
                  <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                  Connecting…
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Navbar — mirrors CharterPrepPage: {showNavbar && <div className='ms-6'><Navbar /></div>} */}
        <div className='ms-6'>
          <Navbar />
        </div>

        <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
          <div className='flex flex-col h-full min-h-0 py-9'>
            <div className='flex flex-col flex-1 min-h-0 ml-[2.5rem] px-14'>
              {session?.error ? (
                <div className='flex flex-col items-center justify-center flex-1 gap-2 text-center'>
                  <p className='text-sm text-red-500'>{session.error}</p>
                  <p className='text-xs text-gray-400'>The student may not have started their prep session yet.</p>
                </div>
              ) : !isConnected && messages.length === 0 ? (
                <div className='flex items-center justify-center flex-1 gap-2 text-gray-400'>
                  <Loader2 className='h-5 w-5 animate-spin' />
                  <span>Connecting to session…</span>
                </div>
              ) : messages.length === 0 ? (
                <div className='flex flex-col items-center justify-center flex-1 gap-2 text-center'>
                  <Sparkles className='h-6 w-6 text-gray-300' />
                  <p className='text-sm text-gray-500'>
                    This student hasn’t started their prep session yet.
                  </p>
                  <p className='text-xs text-gray-400'>
                    Their conversation will appear here live once they begin.
                  </p>
                </div>
              ) : (
                <CharterChatMessages
                  messages={displayMessages}
                  agentName='Preparation Coach'
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>

    {profile && (
      <PrepProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        profile={profile}
        profileApproved={profileApproved}
      />
    )}
    </>
  )
}
